import React, { useState, useEffect } from "react";
import { PaziaBox } from "components/atoms";
import { DaysHeader } from "./DaysHeader";
import { CalendarDayCard, CalendarEvent } from "./CalendarDayCard";

import { MdKeyboardArrowLeft } from "react-icons/md";
import { MdKeyboardArrowRight } from "react-icons/md";
import { PaziaHeader } from "components/atoms/PaziaHeader/PaziaHeader";
import { FilterColors } from "./CalendarFilterButton";
import { CalendarFilters } from "./CalendarFilters";
import { monthNames } from "components/molecules/MonthCalendar";

interface MonthCalendarProps {
  events: CalendarEvent[];
  onMoreEventsClick: (
    date: {
      day: number;
      month: number;
      year: number;
    },
    events: CalendarEvent[],
  ) => void;
}
export const MonthCalendar = ({ events, onMoreEventsClick }: MonthCalendarProps) => {
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth());
  const [year, setYear] = useState(today.getFullYear());
  const [daysInMonth, setDaysInMonth] = useState<number[]>([]);
  const [startingBlankDays, setStartingBlankDays] = useState<number[]>([]);
  const [endingBlankDays, setEndingBlankDays] = useState<number[]>([]);
  const [courseFilters, setCourseFilters] = useState<
    {
      courseId: number;
      courseName: string;
      isSelected: boolean;
      color: FilterColors;
    }[]
  >([]);

  const getEvents = (date: number) => {
    // Filter events by the active filters
    const activeFilters = courseFilters.filter((filter) => filter.isSelected).map((filter) => filter.courseId);
    return events.filter(
      (e) =>
        activeFilters.includes(e.courseId) &&
        new Date(e.eventStart).toDateString() === new Date(year, month, date).toDateString(),
    );
  };

  const getDays = (): void => {
    const days = new Date(year, month + 1, 0).getDate();

    // starting empty cells (previous month)
    let startingDayOfWeek = new Date(year, month).getDay();
    startingDayOfWeek = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1; // Adjust for Monday start
    let startingBlankDaysArray = [];
    for (let i = 1; i <= startingDayOfWeek; i++) {
      startingBlankDaysArray.push(i);
    }

    // ending empty cells (next month)
    const endingDayOfWeek = new Date(year, month + 1, 0).getDay();
    const adjustedEndingDay = endingDayOfWeek === 0 ? 6 : endingDayOfWeek - 1; // Adjust for Monday start
    let endingBlankDaysArray = [];
    for (let i = 1; i < 7 - adjustedEndingDay; i++) {
      endingBlankDaysArray.push(i);
    }

    // current month cells
    let daysArray = [];
    for (let i = 1; i <= days; i++) {
      daysArray.push(i);
    }

    setStartingBlankDays(startingBlankDaysArray);
    setEndingBlankDays(endingBlankDaysArray);
    setDaysInMonth(daysArray);
  };

  // Update course filters based on the currently selected month
  useEffect(() => {
    const eventsInMonth = events.filter((event) => {
      const eventDate = new Date(event.eventStart);
      return eventDate.getMonth() === month && eventDate.getFullYear() === year;
    });

    const courseIds = eventsInMonth.map((event) => event.courseId);
    const uniqueCourseIds = [...new Set(courseIds)];

    const filteredCourses = uniqueCourseIds
      .map((courseId) => {
        const courseItem = eventsInMonth.find((event) => event.courseId === courseId);
        if (!courseItem) return null;
        return { courseId, courseName: courseItem.courseName, isSelected: true, color: courseItem.eventColor };
      })
      .filter((course) => course !== null) as {
      courseId: number;
      courseName: string;
      isSelected: boolean;
      color: FilterColors;
    }[];

    setCourseFilters(filteredCourses);
  }, [month, year, events]);

  useEffect(() => {
    getDays();
  }, [month, year]);

  const toggleFilter = (courseId: number) => {
    setCourseFilters((prevFilters) =>
      prevFilters.map((filter) =>
        filter.courseId === courseId ? { ...filter, isSelected: !filter.isSelected } : filter,
      ),
    );
  };

  return (
    <div className="w-3/4">
      <CalendarHeader month={month} year={year} setMonth={setMonth} setYear={setYear} getDays={getDays} />
      <CalendarFilters filters={courseFilters} onToggleFilter={toggleFilter} />

      <PaziaBox className="!p-0">
        <DaysHeader />

        <div className="grid grid-cols-7 gap-px bg-slate-200 dark:bg-slate-700">
          <DiagonalStripesPattern />
          {startingBlankDays.map((blankday) => (
            <BlankDay key={blankday} />
          ))}
          {daysInMonth.map((day) => (
            <CalendarDayCard
              key={day}
              day={day}
              events={getEvents(day)}
              onMoreClick={(day, events) =>
                onMoreEventsClick(
                  {
                    day,
                    month,
                    year,
                  },
                  events,
                )
              }
            />
          ))}
          {endingBlankDays.map((blankday) => (
            <BlankDay key={blankday} />
          ))}
        </div>
      </PaziaBox>
    </div>
  );
};

const CalendarHeader = ({
  month,
  year,
  setMonth,
  setYear,
  getDays,
}: {
  month: number;
  year: number;
  setMonth: React.Dispatch<React.SetStateAction<number>>;
  setYear: React.Dispatch<React.SetStateAction<number>>;
  getDays: () => void;
}) => {
  return (
    <div className="mb-4 sm:flex sm:items-center sm:justify-between">
      <PaziaHeader>{`${monthNames[month]} ${year}`}</PaziaHeader>

      <div className="grid grid-flow-col justify-start gap-2 sm:auto-cols-max sm:justify-end">
        <button
          className="btn border-slate-200 bg-white px-2.5 text-slate-500 hover:border-slate-300 hover:text-slate-600 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400 dark:hover:border-slate-600 dark:hover:text-slate-300 dark:disabled:border-slate-700 dark:disabled:bg-slate-700 dark:disabled:text-slate-600"
          type="button"
          onClick={() => {
            if (month === 0) {
              setMonth(11);
              setYear(year - 1);
              return;
            }
            setMonth(month - 1);
            getDays();
          }}
        >
          <MdKeyboardArrowLeft size={20} />
        </button>

        <button
          className="btn border-slate-200 bg-white px-2.5 text-slate-500 hover:border-slate-300 hover:text-slate-600 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400 dark:hover:border-slate-600 dark:hover:text-slate-300 dark:disabled:border-slate-700 dark:disabled:bg-slate-700 dark:disabled:text-slate-600"
          type="button"
          onClick={() => {
            if (month === 11) {
              setMonth(0);
              setYear(year + 1);
              return;
            }
            setMonth(month + 1);
            getDays();
          }}
        >
          <MdKeyboardArrowRight size={20} />
        </button>
      </div>
    </div>
  );
};

const BlankDay = () => {
  return (
    <div className="h-20 bg-slate-50 sm:h-28 lg:h-36 dark:bg-slate-800">
      <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
        <rect width="100%" height="100%" fill="url(#stripes)" />
      </svg>
    </div>
  );
};

const DiagonalStripesPattern = () => {
  return (
    <svg className="sr-only">
      <defs>
        <pattern id="stripes" patternUnits="userSpaceOnUse" width="5" height="5" patternTransform="rotate(135)">
          <line
            className="stroke-current text-slate-200 opacity-50 dark:text-slate-700"
            x1="0"
            y="0"
            x2="0"
            y2="5"
            strokeWidth="2"
          />
        </pattern>
      </defs>
    </svg>
  );
};
